import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "~/atoms/dialog";
import { Button } from "~/atoms/button";
import DatePicker from "~/molecules/DatePicker";
import { updateDocumentRelease } from "~/api/v2/requests";
import { toast } from "react-toastify";

interface Props {
  adoptedAt: string | null;
  releaseId: number;
  onChange: () => void;
}

export const SetAdoptionDateModal = ({
  adoptedAt,
  releaseId,
  onChange,
}: Props) => {
  const { t } = useTranslation();
  const [date, setDate] = useState<Date | undefined>(
    adoptedAt ? new Date(adoptedAt) : new Date(),
  );

  const [isOpen, setIsOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleConfirm = async () => {
    try {
      setIsSubmitting(true);
      await updateDocumentRelease(releaseId, {
        adoptedAt: date ?? null,
      });
      toast.success(t(($) => $.toasts.success.adoptionDateSet));
      onChange();
    } catch {
      toast.error(t(($) => $.toasts.error.adoptionDateNotSet));
    } finally {
      setIsSubmitting(false);
      setIsOpen(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button variant="secondary" size="sm">
          {!adoptedAt
            ? t(($) => $.commons.workflow.setAdoptionDate)
            : t(($) => $.commons.workflow.changeAdoptionDate)}
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {t(($) => $.commons.workflow.setAdoptionDate)}
          </DialogTitle>
          <DialogDescription>
            {t(($) => $.commons.workflow.setAdoptionDateDescription)}
          </DialogDescription>
        </DialogHeader>
        <DatePicker value={date} onChange={setDate} />
        <DialogFooter>
          <DialogClose asChild>
            <Button variant="outline">
              {t(($) => $.commons.workflow.cancel)}
            </Button>
          </DialogClose>
          <Button onClick={handleConfirm} disabled={isSubmitting}>
            {t(($) => $.commons.buttons.confirm)}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
